import { useEffect, useState } from 'react';
import {
  Box,
  Download,
  FileClock,
  FileDown,
  FileUp,
  History,
  LockOpen,
  RotateCcw,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Textarea } from '@/components/ui/Textarea';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useAuth } from '@/app/AuthProvider';
import {
  createEngSignedUrl,
  triggerBrowserDownload,
  useDownloadEngFile,
  useEngFileVersions,
  useEngProjectItems,
  useEngSignedUrl,
  useReleaseEngCheckout,
  useRestoreEngFileVersion,
  useUpdateEngFile,
  type EngFile,
  type EngFileVersion,
  type EngFolder,
} from '@/features/engineering/hooks';
import {
  ENG_FILE_CATEGORY_LABELS,
  formatBytes,
  isImageFile,
  isViewable3D,
  type EngFileCategory,
} from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { formatDateTime, formatRelative } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { FileIcon, PersonChip } from './EngParts';
import { folderPathLabel } from './engFileUtils';
import type { ModelViewerTarget } from './ModelViewerDialog';

const NO_ITEM = '__none__';

interface EngFileSheetProps {
  file: EngFile | null;
  folders: EngFolder[];
  onClose: () => void;
  canEdit: boolean;
  locked: boolean;
  canManage: boolean;
  onPreview: (target: ModelViewerTarget) => void;
  onNewVersion: (file: EngFile) => void;
}

export function EngFileSheet({
  file,
  folders,
  onClose,
  canEdit,
  locked,
  canManage,
  onPreview,
  onNewVersion,
}: EngFileSheetProps) {
  return (
    <Sheet open={!!file} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-2xl">
        {file && (
          <FileSheetBody
            file={file}
            folders={folders}
            canEdit={canEdit}
            locked={locked}
            canManage={canManage}
            onPreview={onPreview}
            onNewVersion={onNewVersion}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function FileSheetBody({
  file,
  folders,
  canEdit,
  locked,
  canManage,
  onPreview,
  onNewVersion,
}: Omit<EngFileSheetProps, 'file' | 'onClose'> & { file: EngFile }) {
  const { profile } = useAuth();
  const { data: versions, isLoading } = useEngFileVersions(file.id);
  const { data: projectItems } = useEngProjectItems(file.project_id);
  const download = useDownloadEngFile();
  const release = useReleaseEngCheckout();
  const restore = useRestoreEngFileVersion();
  const update = useUpdateEngFile();

  const [description, setDescription] = useState(file.description ?? '');
  useEffect(() => setDescription(file.description ?? ''), [file.id, file.description]);

  const editable = canEdit && !locked;
  const checkedOutByMe = !!file.checked_out_by && file.checked_out_by === profile?.id;
  const viewable = isViewable3D(file.name);
  const image = isImageFile(file.name);
  const { data: previewUrl } = useEngSignedUrl(image ? file.current_version?.storage_path : null);

  const previewVersion = (v: Pick<EngFileVersion, 'storage_path' | 'file_name' | 'version_no'>) =>
    onPreview({
      storagePath: v.storage_path,
      fileName: v.file_name,
      title: `${file.name} · v${v.version_no}`,
      subtitle: file.description ?? undefined,
    });

  const downloadVersion = async (v: EngFileVersion) => {
    try {
      const url = await createEngSignedUrl(v.storage_path, v.file_name);
      triggerBrowserDownload(url, v.file_name);
    } catch (err) {
      toast.error(formatError(err, 'Could not download that version.'));
    }
  };

  const saveDescription = () => {
    update.mutate(
      { id: file.id, projectId: file.project_id, patch: { description } },
      {
        onSuccess: () => toast.success('Description saved.'),
        onError: (err) => toast.error(formatError(err)),
      },
    );
  };

  return (
    <>
      <SheetHeader className="pr-12">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-info-soft/30 text-info">
            <FileIcon category={file.category} className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <SheetTitle className="break-all text-base">{file.name}</SheetTitle>
            <SheetDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>{ENG_FILE_CATEGORY_LABELS[file.category as EngFileCategory] ?? file.category}</span>
              <span>·</span>
              <span>v{file.version_count}</span>
              <span>·</span>
              <span>{formatBytes(file.current_version?.size_bytes)}</span>
              <span>·</span>
              <span>{folderPathLabel(file.folder_id, folders)}</span>
            </SheetDescription>
          </div>
        </div>
      </SheetHeader>

      <SheetBody className="space-y-6">
        {file.checked_out_by && (
          <div
            className={cn(
              'flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm',
              checkedOutByMe
                ? 'border-info-soft bg-info-soft/20 text-info'
                : 'border-fresh-salmon/50 bg-fresh-salmon/10 text-fresh-salmon-dark',
            )}
          >
            <span className="inline-flex items-center gap-2">
              <FileClock className="h-4 w-4" />
              {checkedOutByMe ? 'You checked this out' : 'Checked out by'}{' '}
              {!checkedOutByMe && <PersonChip person={file.checked_out_profile} size="xs" />}
              <span className="text-xs opacity-80">{formatRelative(file.checked_out_at)}</span>
            </span>
            {(checkedOutByMe || canManage) && editable && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  release.mutate({ file }, { onError: (err) => toast.error(formatError(err)) })
                }
                disabled={release.isPending}
              >
                <LockOpen className="h-3.5 w-3.5" />
                Release check-out
              </Button>
            )}
          </div>
        )}

        {image && previewUrl && (
          <div className="overflow-hidden rounded-lg border border-border bg-secondary/40">
            <img src={previewUrl} alt={file.name} className="mx-auto max-h-72 object-contain" />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {viewable && file.current_version && (
            <Button size="sm" onClick={() => previewVersion(file.current_version!)}>
              <Box className="h-3.5 w-3.5" />
              View in 3D
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={!file.current_version || download.isPending}
            onClick={() =>
              download.mutate({ file, checkOut: false }, { onError: (err) => toast.error(formatError(err)) })
            }
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </Button>
          {editable && (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={!file.current_version || download.isPending || (!!file.checked_out_by && !checkedOutByMe)}
                onClick={() =>
                  download.mutate(
                    { file, checkOut: true },
                    {
                      onSuccess: () => toast.success('Downloaded and checked out. Upload it back when you’re done.'),
                      onError: (err) => toast.error(formatError(err)),
                    },
                  )
                }
              >
                <FileDown className="h-3.5 w-3.5" />
                Download to edit
              </Button>
              <Button size="sm" variant={checkedOutByMe ? 'default' : 'outline'} onClick={() => onNewVersion(file)}>
                <FileUp className="h-3.5 w-3.5" />
                {checkedOutByMe ? 'Check in new version' : 'Upload new version'}
              </Button>
            </>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="eng-file-description">Description</Label>
          {editable ? (
            <>
              <Textarea
                id="eng-file-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this file is, units, which part it belongs to…"
                className="min-h-[64px]"
              />
              {description !== (file.description ?? '') && (
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={saveDescription} disabled={update.isPending}>
                    <Save className="h-3.5 w-3.5" />
                    Save
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{file.description || 'No description.'}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>Catalog item</Label>
          {editable && (projectItems ?? []).length > 0 ? (
            <Select
              value={file.item_id ?? NO_ITEM}
              onValueChange={(v) =>
                update.mutate(
                  { id: file.id, projectId: file.project_id, patch: { item_id: v === NO_ITEM ? null : v } },
                  { onError: (err) => toast.error(formatError(err)) },
                )
              }
            >
              <SelectTrigger aria-label="Catalog item">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ITEM}>Not tied to a specific item</SelectItem>
                {(projectItems ?? []).map((link) => (
                  <SelectItem key={link.item_id} value={link.item_id}>
                    {link.item?.sku} · {link.item?.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm text-muted-foreground">
              {file.item ? (
                <>
                  <span className="font-mono text-xs">{file.item.sku}</span> · {file.item.name}
                </>
              ) : editable ? (
                'Link a catalog item to the project first (Overview → Catalog items), then pick it here.'
              ) : (
                'Not tied to a specific item.'
              )}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <History className="h-4 w-4 text-info" />
              Version history
            </Label>
            <span className="text-xs text-muted-foreground">
              {file.version_count} version{file.version_count === 1 ? '' : 's'}
            </span>
          </div>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <ol className="divide-y divide-border rounded-lg border border-border">
              {(versions ?? []).map((v) => {
                const current = v.id === file.current_version_id;
                return (
                  <li key={v.id} className={cn('px-3 py-2.5', current && 'bg-info-soft/10')}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={current ? 'info' : 'outline'} className="font-mono text-[11px]">
                        v{v.version_no}
                      </Badge>
                      {current && <span className="text-xs font-medium text-info">Current</span>}
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(v.created_at)} · {formatBytes(v.size_bytes)}
                      </span>
                      <span className="ml-auto flex items-center gap-1">
                        {isViewable3D(v.file_name) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            aria-label={`View v${v.version_no} in 3D`}
                            onClick={() => previewVersion(v)}
                          >
                            <Box className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          aria-label={`Download v${v.version_no}`}
                          onClick={() => void downloadVersion(v)}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        {editable && !current && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            aria-label={`Restore v${v.version_no}`}
                            disabled={restore.isPending}
                            onClick={() => {
                              if (!window.confirm(`Restore v${v.version_no} as the newest version?`)) return;
                              restore.mutate(
                                { file, version: v },
                                {
                                  onSuccess: (nv) => toast.success(`Restored as v${nv.version_no}.`),
                                  onError: (err) => toast.error(formatError(err)),
                                },
                              );
                            }}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <PersonChip person={v.uploader} size="xs" />
                      {v.file_name.toLowerCase() !== file.name.toLowerCase() && (
                        <span className="font-mono">{v.file_name}</span>
                      )}
                    </div>
                    <p className={cn('mt-1 text-sm', v.change_summary ? 'text-foreground' : 'italic text-muted-foreground')}>
                      {v.change_summary ?? 'No change summary'}
                    </p>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </SheetBody>
    </>
  );
}
